/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartPie,
  HiOutlineFolder,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineViewGrid,
} from "react-icons/hi";

import ListPage from "../../../layout/ListPage";
import { cn } from "../../../ui";
import type { Action, Column } from "../../../ui";
import { _PAGE_SIZE_ } from "../../../lib/other/tools";
import { useBaseLevelContext } from "../../../context/levels/BaseLevelContext";
import {
  pushAddLevel,
  pushEditLevel,
  pushSingleLevelDashboard,
} from "../../../lib/sysNavigation/SysNavigation";

type LevelKind = "first" | "second" | "third" | "fourth";

type LevelRow = {
  name?: string;
  first_level_id?: number | string;
  second_level_id?: number | string;
  third_level_id?: number | string;
  fourth_level_id?: number | string;
  [key: string]: unknown;
};

const TABS: Array<{ title: string; route: string; value: LevelKind }> = [
  { title: "First", route: "/levels/first", value: "first" },
  { title: "Second", route: "/levels/second", value: "second" },
  { title: "Third", route: "/levels/third", value: "third" },
  { title: "Fourth", route: "/levels/fourth", value: "fourth" },
];

const addRoute = "/level/add";
const editRoute = "/level/edit";
const contentRoute = "/dosee/data";
const dashboardRoute = "/level/dashboard";

const idOfRow = (row: LevelRow, level: LevelKind) =>
  level === "first"
    ? row.first_level_id
    : level === "second"
    ? row.second_level_id
    : level === "third"
    ? row.third_level_id
    : row.fourth_level_id;

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    fetchLevels,
    allLevels,
    count,
    setTemporaryLevelName,
    setTemporaryLevel,
    setAllLevels,
    deleteOne,
  } = useBaseLevelContext() as any;

  const [selectedLevel, setSelectedLevel] = useState<LevelKind>("first");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!allLevels) {
      fetchLevels({ page: 1, pageSize: _PAGE_SIZE_, level: selectedLevel });
    }
  }, [allLevels, selectedLevel]);

  const rows = useMemo<LevelRow[] | null>(() => {
    if (!Array.isArray(allLevels)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return allLevels as LevelRow[];
    return (allLevels as LevelRow[]).filter((r) =>
      String(r.name ?? "").toLowerCase().includes(q),
    );
  }, [allLevels, query]);

  const switchTab = (next: LevelKind) => {
    setSelectedLevel(next);
    setAllLevels(null);
    setPage(1);
    setQuery("");
  };

  const tabs = (
    <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
      {TABS.map((tab) => {
        const active = selectedLevel === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => switchTab(tab.value)}
            className={cn(
              "rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition",
              active
                ? "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )}
          >
            {tab.title}
          </button>
        );
      })}
    </div>
  );

  const columns: Column<LevelRow>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineViewGrid className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name ?? "—"}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: LevelRow) => {
    const id = idOfRow(row, selectedLevel);
    const { status } = await deleteOne({ level_id: id, level: selectedLevel });
    if (status === 200) {
      await fetchLevels({ page, pageSize, level: selectedLevel });
    }
  };

  const rowActions = (row: LevelRow): Action[] => [
    {
      label: "Dashboard",
      icon: <HiOutlineChartPie className="h-3.5 w-3.5" />,
      onClick: () => {
        pushSingleLevelDashboard({ data: row, level: selectedLevel });
        navigate(dashboardRoute);
      },
    },
    {
      label: "Content",
      icon: <HiOutlineFolder className="h-3.5 w-3.5" />,
      onClick: () => {
        pushAddLevel(selectedLevel);
        setTemporaryLevel({ ...row, currentLevel: selectedLevel });
        navigate(contentRoute);
      },
    },
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushAddLevel(selectedLevel);
        pushEditLevel({ ...row });
        setTemporaryLevel({ ...row, currentLevel: selectedLevel });
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name ?? "this level"}?`,
        description: "This action cannot be undone.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<LevelRow>
      title="Administrative Levels"
      subtitle="First through fourth level divisions used by content."
      icon={<HiOutlineViewGrid className="h-5 w-5" />}
      actions={[
        {
          label: `Add ${selectedLevel} level`,
          variant: "primary",
          onClick: () => {
            pushAddLevel(selectedLevel);
            setTemporaryLevelName(selectedLevel);
            navigate(addRoute);
          },
        },
      ]}
      toolbar={tabs}
      rows={rows}
      columns={columns}
      rowKey={(r) => String(idOfRow(r, selectedLevel) ?? r.name)}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: `Search ${selectedLevel} levels…`,
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchLevels({ page: p, pageSize: ps, level: selectedLevel });
        },
      }}
      empty="No levels found for this tab."
    />
  );
};

export default Overview;
